import React from 'react';
import { Truck, Search, MapPin, Gauge, ScanBarcode } from 'lucide-react';
import type { SamsaraVehicle } from '@/types';
import { QRScanner } from '@/components/common/QRScanner';
import { VehicleConfirmModal } from '@/components/common/VehicleConfirmationModal';
import { useQRVehicleScan } from '@/hooks/useQRVehicleScan';
import { useAssignedVehicles } from '@/hooks/useAssignedVehicles';
import { useRole } from '@/hooks/useRole';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

export function VehiclesPage() {
  const { vehicles, loading } = useAssignedVehicles();
  const { isAdmin } = useRole();
  const [search, setSearch] = React.useState('');
  const [selectedVehicle, setSelectedVehicle] = React.useState<SamsaraVehicle | null>(null);

  const {
    scannerOpen, scannedVehicle, looking,
    openScanner, handleScan, closeScanner, closeConfirm,
  } = useQRVehicleScan();

  const filtered = vehicles.filter(
    (v) =>
      v.name?.toLowerCase().includes(search.toLowerCase()) ||
      v.vin?.toLowerCase().includes(search.toLowerCase()) ||
      `${v.make ?? ''} ${v.model ?? ''}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 dark:bg-gray-800 dark:text-gray-100 min-h-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-wire3-navy dark:text-wire3-green">Fleet Vehicles</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {loading ? 'Loading...' : `${vehicles.length} vehicle${vehicles.length !== 1 ? 's' : ''} in fleet`}
          </p>
        </div>
        <button onClick={openScanner} className="btn-secondary flex items-center gap-2">
          <ScanBarcode className="h-4 w-4" />
          Scan VIN
        </button>
      </div>

      <div className="relative mt-4">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search vehicles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10"
        />
      </div>

      <div className="mt-4 flex flex-col gap-3">
        {loading && <LoadingSpinner message="Loading fleet..." />}

        {/* VIN scan lookup overlay */}
        {looking && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
            <div className="rounded-xl bg-white dark:bg-gray-800 px-6 py-4 shadow-xl flex items-center gap-3">
              <LoadingSpinner size="sm" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Looking up vehicle...</p>
            </div>
          </div>
        )}

        {scannerOpen && <QRScanner onScan={handleScan} onClose={closeScanner} />}

        {scannedVehicle && (
          <VehicleConfirmModal
            vehicle={scannedVehicle}
            onConfirm={() => { closeConfirm(); setSelectedVehicle(scannedVehicle); }}
            onClose={closeConfirm}
          />
        )}

        {selectedVehicle && !scannedVehicle && (
          <VehicleConfirmModal
            vehicle={selectedVehicle}
            onConfirm={() => setSelectedVehicle(null)}
            onClose={() => setSelectedVehicle(null)}
            confirmLabel="Close"
            hideCancel
          />
        )}

        {!loading && filtered.length === 0 && vehicles.length > 0 && (
          <div className="flex flex-col items-center py-12 text-gray-400">
            <Truck className="mb-2 h-10 w-10" />
            <p>No vehicles match your search</p>
          </div>
        )}

        {!loading && filtered.map((v) => (
          <button
            key={v.id}
            onClick={() => setSelectedVehicle(v)}
            className="card dark:bg-gray-750 dark:border-gray-700 flex items-center gap-4 text-left transition-shadow hover:shadow-md dark:hover:bg-gray-700 active:scale-[0.99] w-full"
          >
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-wire3-navy/5 dark:bg-wire3-green/10">
              <Truck className="h-7 w-7 text-wire3-navy dark:text-wire3-green" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-wire3-navy dark:text-wire3-green">{v.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{v.year} {v.make} {v.model}</p>
              <div className="mt-1 flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
                {v.odometerMeters && (
                  <span className="flex items-center gap-1">
                    <Gauge className="h-3 w-3" />
                    {Math.round(v.odometerMeters / 1609.34).toLocaleString()} mi
                  </span>
                )}
                {v.gps && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {v.gps.latitude.toFixed(4)}, {v.gps.longitude.toFixed(4)}
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs font-mono text-gray-400 dark:text-gray-500">{v.licensePlate}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
